import ContentCard from '@src/components/cards/contentCard';
import VoteBadge from '@src/components/atoms/voteBadge';
import type { IDRepVote } from '@src/store/drep/types';
import { seperateDateTime, timeAgo } from '@src/utils/date';
import AnchorLink from '@src/components/atoms/links/anchor-link';
import ExternalLink from '@src/components/icons/externalLink';
import EmptyContent from '@src/components/cards/emptyContentCard';
import ErrorContent from '@src/components/cards/errorContentCard';
import LoadingContent from '@src/components/cards/loadingContentCard';
import { noContentDescription } from '@src/constants';
import { determineExternalExplorerLink } from '@src/utils/network';
import environments from '@src/configs/environments';
import { getNetworkNameFromURL } from '@src/utils/network';
import Link from 'next/link';
import { EMPTY_SEO_VOTES, type SeoVotes } from '@src/lib/seo/votes';
import type { IPaginationDetails } from '../atoms/pagination';
import Pagination from '../atoms/pagination';

/**
 * A row from either source: the client vote feed, or the server-resolved rows
 * this tab renders before that feed arrives. The server rows carry no
 * transaction hash, so the explorer link is per-row optional.
 */
interface VoteRow {
  govActionId: string;
  title?: string | null;
  voteType: string;
  govActionType?: string | null;
  time?: string | null;
  voteTxHash?: string | null;
}

interface DRepVotesProps {
  votes: IDRepVote[];
  isOwner: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /**
   * Server-rendered first page plus the proposal titles and canonical URLs the
   * vote feed does not carry. Present in the initial HTML, so this list is
   * what a crawler follows from a profile into the governance corpus.
   */
  seoVotes?: SeoVotes;
  paginationDetails: IPaginationDetails;
}

export default function DRepVotes({
  votes,
  isOwner,
  isLoading,
  isError,
  onRetry,
  seoVotes = EMPTY_SEO_VOTES,
  paginationDetails,
}: DRepVotesProps) {
  const rows: VoteRow[] = votes.length > 0 ? votes : seoVotes.rows;

  if (isError) return <ErrorContent onRetry={onRetry} isRetrying={isLoading} />;
  if (isLoading && rows.length === 0) return <LoadingContent />;
  if (rows.length === 0)
    return (
      <EmptyContent
        description={
          isOwner ? noContentDescription.owner.other : noContentDescription.user
        }
      />
    );

  const totalCount = paginationDetails.totalCount || seoVotes.total;

  return (
    <ContentCard>
      <table className="w-full rounded-lg border dark:border-dark-neutral-700">
        <thead>
          <tr>
            <th className="min-w-[69px]">Vote</th>
            <th className="min-w-[77px]">Type</th>
            <th className="min-w-[177px] 2xl:min-w-[390px]">GovAction</th>
            <th className="min-w-[144px]">Transaction</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((vote, index) => {
            const dateTime = vote.time
              ? seperateDateTime(vote.time)
              : { date: '', time: '' };
            // The vote feed carries `<txHash>#<index>` and usually no title;
            // the proposal index supplies the bech32 URL and the real title.
            const link = seoVotes.links[vote.govActionId];
            return (
              <tr key={index}>
                <td>
                  <VoteBadge vote={vote.voteType} />
                </td>
                <td className="label12 font-medium dark:text-white">
                  {vote.govActionType}
                </td>
                <td className="body14 font-medium dark:text-white">
                  <Link
                    href={
                      link?.href || `/governance_actions/${vote.govActionId}`
                    }
                    className="hover:underline"
                  >
                    {link?.title || vote.title || '-'}
                  </Link>
                </td>
                <td className="flex items-center">
                  {vote.time && (
                    <div className="flex flex-col">
                      <span className="relative-time">
                        {timeAgo(vote.time)}
                      </span>
                      <span className="transaction-date">
                        {dateTime.date} | {dateTime.time}
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  {vote.voteTxHash && (
                    <AnchorLink
                      href={`${determineExternalExplorerLink(
                        environments.CARDANO_NETWORK ||
                          (typeof window !== 'undefined'
                            ? getNetworkNameFromURL(window.location.href)
                            : 'MAINNET')
                      )}/transaction/${vote.voteTxHash}?tab=votes`}
                      target="new"
                    >
                      <ExternalLink className="h-[14px] w-[14px] cursor-pointer text-dark-neutral-700 dark:text-white" />
                    </AnchorLink>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        page={paginationDetails.currentPage || 1}
        totalPages={parseInt(Math.ceil(totalCount / 10).toFixed(0))}
        onNext={paginationDetails.onNext}
        onPrevious={paginationDetails.onPrevious}
      />
    </ContentCard>
  );
}
